import type { FC, ReactElement, KeyboardEvent, MouseEvent } from 'react'
import { useState } from 'react'

import { config } from '@skytech/config'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import ArrowRightCircleIcon from 'mdi-react/ArrowRightCircleIcon'
import { twJoin } from 'tailwind-merge'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useSearchParamsByKey } from '@/hooks/searchParams'
import queries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'

const isDevEnv = config.ENVIRONMENT === 'dev'

type DropdownOption = {
  value: string | number
  label: ReactElement
  isDisabled: boolean
}

export const ActionsDropdown: FC<{
  placeholder?: string
  isDisabled: boolean
  isSendSMSAvailable: boolean
  onChange: (value: string | number) => void
}> = ({ placeholder, isDisabled, isSendSMSAvailable, onChange }) => {
  const { whiteLabel } = useAppContext()
  const [queryAssetOwnership] = useSearchParamsByKey('assetOwnership')
  const [isOpen, setIsOpen] = useState(false)

  const options: DropdownOption[] = [
    {
      value: 'SendSMS',
      label: <Translation id="Send SMS" />,
      isDisabled: !isSendSMSAvailable,
    },
  ]
  // TODO: enable for production
  isDevEnv &&
    options.push({
      value: 'EndOfLease',
      label: <Translation id="action/End of lease" />,
      isDisabled: false,
    })

  const { data } = useQuery(queries.adminLeasedAssetList(), adminResources.getLeasedAssetList, {
    enabled: queryAssetOwnership === 'LEASED',
  })

  if (data?.activeEndingOptions && data?.activeEndingOptions.length > 0) {
    const activeOptions = mapOptions(data?.activeEndingOptions)
    options.push(...activeOptions)
  }

  // TODO: enable for production
  // Shows Actions for the Dev environment only
  const showForDevEnv = (el: string | number) => (el === 'SendSMS' ? true : isDevEnv)

  const handleSelect = (e: MouseEvent | KeyboardEvent, value: string | number) => {
    e.stopPropagation()
    onChange(value)
    setIsOpen(false)
  }
  const handleKeyDownSelect = (e: KeyboardEvent<HTMLLIElement>, value: string | number) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleSelect(e, value)
    }
  }
  return (
    <div className="w-full">
      {/* Dropdown Button */}
      <Button
        className={twJoin(
          'relative m-0 flex w-full min-w-[170px] justify-between',
          whiteLabel === 'mobit' && 'disabled:brightness-[1]',
          whiteLabel === 'tietoevry' && 'text-white',
          whiteLabel === 'arp' &&
            'hover:brightness-[0.9] focus:brightness-[0.9] active:brightness-[0.9]'
        )}
        disabled={isDisabled}
        variant="primary"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        Icon={<ArrowRightCircleIcon />}
      >
        <span className="ml-4">{placeholder}</span>
      </Button>

      {/* Dropdown Options */}
      {isOpen && !isDisabled && (
        <div
          role="menu"
          className="absolute z-10 mt-2 flex w-[130px] w-full min-w-[170px] max-w-[180px] flex-col rounded-md border border-gray-300 bg-white p-0 shadow-lg"
        >
          <ul className="m-0 list-none p-0">
            {options.map(
              ({ value, label, isDisabled }: DropdownOption, idx: number) =>
                showForDevEnv(value) && (
                  <li
                    key={idx}
                    role="menuitem"
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                    className={twJoin(
                      'px-4 py-2',
                      `text-${whiteLabel}`,
                      isDisabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer hover:bg-gray-100'
                    )}
                    onClick={e => !isDisabled && handleSelect(e, value)}
                    onKeyDown={e => !isDisabled && handleKeyDownSelect(e, value)}
                  >
                    {label}
                  </li>
                )
            )}
          </ul>
        </div>
      )}
    </div>
  )
}

const mapOptions = (options: (string | number)[]) =>
  options.map(option => {
    const label =
      typeof option === 'string' ? (
        <Translation id={`action/${option}`} />
      ) : (
        <Translation
          id="Extends {count} {count, plural, one {month} other {months}}"
          count={String(option)}
        />
      )
    return {
      value: option,
      label,
      isDisabled: false,
    }
  })
